import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Link,
  palette,
  Stack,
  StatusChip,
  Typography,
  type StatusKind,
} from '@munifocus/ui';
import type { DocumentSignoffView, SignoffStatus } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { DecisionDialog } from '../components/signoff/DecisionDialog';
import { useSignoffQueue } from '../lib/signoff-api';
import { formatDateTime } from '../lib/relative-time';

const STATUS_KIND: Record<SignoffStatus, StatusKind> = {
  pending: 'pending',
  approved: 'approved',
  changes_requested: 'warning',
  rejected: 'rejected',
  withdrawn: 'neutral',
};

function Row({ item, action }: { item: DocumentSignoffView; action?: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${palette.border.line}` }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', gap: 1, alignItems: { sm: 'center' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t(`gov.forms.kind.${item.formKind}`, { defaultValue: item.formKind })}
            {' · '}
            {t('gov.signoff.version', { version: item.formVersion })}
          </Typography>
          <Typography variant="caption" sx={{ color: palette.text.muted, display: 'block' }}>
            <Link component={RouterLink} to={`/files/${item.fileId}`} underline="hover">
              {item.fileReference}
            </Link>
            {' — '}
            {t('gov.signoff.submittedBy', {
              name: item.submittedByName,
              at: formatDateTime(item.submittedAt, i18n.language),
            })}
          </Typography>
          {item.submitNote && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {item.submitNote}
            </Typography>
          )}
          {item.decisionComment && item.status !== 'pending' && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {item.reviewerName}: {item.decisionComment}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          <StatusChip
            status={STATUS_KIND[item.status]}
            label={t(`gov.signoff.status.${item.status}`)}
          />
          {action}
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * The review queue: documents waiting for the caller's signature at every
 * municipality where they hold signing authority for that kind, and the
 * caller's own submissions with where each stands.
 */
export function SignoffQueue() {
  const { t } = useTranslation();
  const queue = useSignoffQueue();
  const [reviewing, setReviewing] = useState<DocumentSignoffView | null>(null);

  return (
    <AppShell>
      {/* The topbar holds the title (the page's one h1); this says what the
          screen is for. */}
      <PageIntro>{t('gov.signoff.queue.subtitle')}</PageIntro>

      {queue.isLoading && <CircularProgress size={24} />}
      {queue.isError && <Alert severity="error">{t('gov.signoff.queue.loadFailed')}</Alert>}
      {queue.data && !queue.data.enabled && (
        <Alert severity="info">{t('gov.signoff.queue.disabled')}</Alert>
      )}

      {queue.data?.enabled && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
              {t('gov.signoff.queue.waiting', { count: queue.data.pending.length })}
            </Typography>
            <HairlineCard sx={{ p: 0 }}>
              {queue.data.pending.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.signoff.queue.emptyWaiting')}
                </Typography>
              ) : (
                queue.data.pending.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    action={
                      <Button size="small" variant="contained" onClick={() => setReviewing(item)}>
                        {t('gov.signoff.review')}
                      </Button>
                    }
                  />
                ))
              )}
            </HairlineCard>
          </Box>

          <Box>
            <Typography variant="subtitle2" component="h2" sx={{ mb: 1 }}>
              {t('gov.signoff.queue.mine')}
            </Typography>
            <HairlineCard sx={{ p: 0 }}>
              {queue.data.mine.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.signoff.queue.emptyMine')}
                </Typography>
              ) : (
                queue.data.mine.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    action={
                      item.signedUrl ? (
                        <Link
                          href={item.signedUrl}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                        >
                          {t('gov.signoff.openSigned')}
                        </Link>
                      ) : undefined
                    }
                  />
                ))
              )}
            </HairlineCard>
          </Box>
        </Stack>
      )}

      {reviewing && <DecisionDialog signoff={reviewing} open onClose={() => setReviewing(null)} />}
    </AppShell>
  );
}
